import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Badge,
  Menu,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useCart } from '../services/CartContext';
import { performLogout } from '../services/authService';

const NAV_LINKS = [
  { label: 'Home', href: '#top', value: 'home' },
  { label: 'Coupons', href: '#coupons', value: 'coupons' },
  { label: 'FAQ', href: '#faq', value: 'faq' },
  { label: 'About Us', href: '#about', value: 'about' },
  { label: 'Contact Us', href: '#contact', value: 'contact' },
];

export default function Navbar({ onNavigate, currentPage }) {
  const { cartCount, setIsCartOpen } = useCart();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const isLoggedIn = !!(localStorage.getItem('token') || sessionStorage.getItem('token'));
  const links = [
    ...NAV_LINKS,
    ...(isLoggedIn ? [{ label: 'My Coupon', href: '#my-coupon', value: 'my-coupon' }] : [])
  ];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    performLogout();
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const logoMark = (
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: '50%',
        background: 'conic-gradient(from 200deg, #D4A24C, #E4572E, #2F8F76, #D4A24C)',
        display: 'grid',
        placeItems: 'center',
        position: 'relative',
        mr: 1.2,
        '&::after': {
          content: '""',
          width: 12,
          height: 12,
          backgroundColor: '#0F2E2B',
          borderRadius: '50%',
        },
      }}
    />
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: 'rgba(15, 46, 43, 0.82)',
          backdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(245, 241, 230, 0.14)',
          color: '#F5F1E6',
          top: 0,
          zIndex: 1100,
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            maxWidth: 1240,
            width: '100%',
            mx: 'auto',
            px: { xs: 2.5, md: 4 },
            py: 1,
          }}
        >
          {/* Left Side: Hamburger (Mobile) + Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  mr: 0.5,
                  color: '#F5F1E6',
                  p: 0.8,
                  '&:hover': {
                    color: '#D4A24C',
                    backgroundColor: 'rgba(212, 162, 76, 0.08)',
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Logo */}
            <Box
              component="a"
              href="#top"
              onClick={(e) => {
                if (onNavigate) {
                  e.preventDefault();
                  onNavigate('home');
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: 'inherit',
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                fontSize: { xs: '20px', sm: '21px' },
                letterSpacing: '-0.01em',
              }}
            >
              {logoMark}
              iSoret
            </Box>
          </Box>

          {/* Desktop Links */}
          {!isMobile && (
            <Box component="nav" sx={{ display: 'flex', gap: 3.8 }}>
              {links.map((link) => (
                <Box
                  key={link.label}
                  component="a"
                  href={link.href}
                  onClick={(e) => {
                    if (onNavigate) {
                      e.preventDefault();
                      onNavigate(link.value);
                    }
                  }}
                  sx={{
                    fontSize: '14.5px',
                    color: currentPage === link.value ? '#D4A24C' : 'rgba(245, 241, 230, 0.66)',
                    fontWeight: 500,
                    textDecoration: 'none',
                    transition: 'color 0.2s',
                    '&:hover': {
                      color: '#F5F1E6',
                    },
                  }}
                >
                  {link.label}
                </Box>
              ))}
            </Box>
          )}

          {/* Right Side Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              color="inherit"
              aria-label="cart"
              onClick={() => setIsCartOpen(true)}
              sx={{
                color: 'rgba(245, 241, 230, 0.8)',
                transition: 'color 0.2s, transform 0.2s',
                '&:hover': {
                  color: '#D4A24C',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Badge badgeContent={cartCount} color="secondary" showZero={false}>
                <ShoppingCartIcon />
              </Badge>
            </IconButton>

            {isLoggedIn ? (
              <>
                <IconButton
                  color="inherit"
                  aria-label="profile-menu"
                  onClick={handleMenuOpen}
                  sx={{
                    color: '#D4A24C',
                    transition: 'color 0.2s, transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <AccountCircleIcon fontSize="large" />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={openMenu}
                  onClose={handleMenuClose}
                  disableScrollLock={true}
                  slotProps={{
                    paper: {
                      sx: {
                        background: '#153A35',
                        border: '1px solid rgba(245, 241, 230, 0.14)',
                        color: '#F5F1E6',
                        mt: 1,
                        minWidth: 150,
                        '& .MuiMenuItem-root': {
                          fontSize: '14.5px',
                          fontWeight: 500,
                          py: 1,
                          '&:hover': {
                            backgroundColor: 'rgba(245, 241, 230, 0.05)',
                            color: '#D4A24C',
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem onClick={() => { handleMenuClose(); if (onNavigate) onNavigate('profile'); }}>Profile</MenuItem>
                  <MenuItem onClick={() => { handleMenuClose(); if (onNavigate) onNavigate('wallet'); }}>Wallet</MenuItem>
                  <MenuItem onClick={() => { handleMenuClose(); if (onNavigate) onNavigate('redeemed-coupons'); }}>Redeemed Coupons</MenuItem>
                  {/* <MenuItem onClick={handleMenuClose}>Invite</MenuItem> */}
                  <MenuItem onClick={handleLogout} sx={{ borderTop: '1px solid rgba(245, 241, 230, 0.08)' }}>Logout</MenuItem>
                </Menu>
              </>
            ) : (
              <IconButton
                color="inherit"
                aria-label="login"
                onClick={() => onNavigate && onNavigate('login')}
                sx={{
                  color: currentPage === 'login' ? '#D4A24C' : 'rgba(245, 241, 230, 0.8)',
                  transition: 'color 0.2s, transform 0.2s',
                  '&:hover': {
                    color: '#D4A24C',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <PersonIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '75%', sm: '50%', md: '340px' },
            backgroundColor: '#0F2E2B',
            color: '#F5F1E6',
            p: 3,
            boxSizing: 'border-box',
            borderRight: '1px solid rgba(245, 241, 230, 0.14)',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: '20px' }}>
            {logoMark}
            iSoret
          </Box>
          <IconButton onClick={handleDrawerToggle} color="inherit">
            <CloseIcon />
          </IconButton>
        </Box>

        <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 0 }}>
          {links.map((link) => (
            <ListItem key={link.label} disablePadding>
              <ListItemButton
                component="a"
                href={link.href}
                onClick={(e) => {
                  if (onNavigate) {
                    e.preventDefault();
                    onNavigate(link.value);
                  }
                  handleDrawerToggle();
                }}
                sx={{
                  borderRadius: 1,
                  px: 2,
                  py: 0.5,
                  color: currentPage === link.value ? '#D4A24C' : 'rgba(245, 241, 230, 0.66)',
                  '&:hover': {
                    color: '#F5F1E6',
                    backgroundColor: 'rgba(245, 241, 230, 0.05)',
                  },
                }}
              >
                <ListItemText
                  primary={link.label}
                  primaryTypographyProps={{
                    fontSize: '16px',
                    fontWeight: 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          {isLoggedIn && (
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  handleDrawerToggle();
                  performLogout();
                }}
                sx={{
                  borderRadius: 1,
                  px: 2,
                  py: 0.5,
                  color: '#E4572E',
                  '&:hover': {
                    backgroundColor: 'rgba(228, 87, 46, 0.1)',
                  },
                }}
              >
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{
                    fontSize: '16px',
                    fontWeight: 600,
                  }}
                />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Drawer>
    </>
  );
}
